/**
 * Strict structured-output contract for AI import-row mapping (DEV-3605).
 *
 * The same Zod schema drives OpenAI's Responses `json_schema` format (via
 * {@link z.toJSONSchema}) and validates the model response. Strict mode
 * forbids optional keys, so unknown values are `z.nullable(...)`.
 *
 * Product ids are validated against the Boverket/CO2data.fi catalogue after
 * parse — the schema cannot express that allowlist.
 */

import { z } from 'zod';
import { AI_IMPORT_MAPPING_UNSUPPORTED_REASONS } from '@shared/models/ai-import-mapping.model';

const sourceSchema = z
  .object({
    title: z
      .string()
      .describe('Short source name, e.g. manufacturer datasheet or Boverket.'),
    url: z
      .nullable(z.string())
      .describe('URL when the source is on the web; null for catalogue-only.'),
  })
  .describe('A cited source for the product match or conversion factor.');

const factorSchema = z
  .object({
    quantityFactor: z
      .number()
      .describe(
        'value_to = qty_from * quantityFactor. Must come from a cited source; never invent density, thickness, or piece weight.',
      ),
    unitTo: z
      .enum(['kg', 'm³', 'm²', 'm', 'pcs', 'kWh'])
      .describe('Product calculation unit the factor converts into.'),
    confidence: z.number().describe('0–1 confidence in the factor.'),
    sources: z
      .array(sourceSchema)
      .describe('Sources that justify the numeric factor.'),
  })
  .describe(
    'Conversion from the import unit to the product unit. Null when native conversion exists, the factor is unknown, or the row is unsupported.',
  );

const suggestionItemSchema = z.object({
  productId: z
    .nullable(z.string())
    .describe(
      'Exact id from the provided catalogue, or null when unsupported / unknown.',
    ),
  productConfidence: z
    .nullable(z.number())
    .describe(
      '0–1 confidence in the product match; null when productId is null.',
    ),
  factor: z.nullable(factorSchema),
  rationale: z
    .string()
    .describe('Short justification for a human reviewer (Swedish or English).'),
  sources: z
    .array(sourceSchema)
    .describe('Sources for the product identity. Empty when catalogue-only.'),
  unsupportedReason: z
    .nullable(z.enum(AI_IMPORT_MAPPING_UNSUPPORTED_REASONS))
    .describe(
      'Set when this is not a material-product mapping; otherwise null.',
    ),
});

/** Model output before catalogue validation and conversion-factor resolution. */
export const aiImportMappingOutputSchema = z.object({
  suggestions: z
    .array(suggestionItemSchema)
    .describe(
      'One to three ranked alternatives. First is the primary suggestion.',
    ),
});

export type AiImportMappingModelOutput = z.infer<
  typeof aiImportMappingOutputSchema
>;

export const AI_IMPORT_MAPPING_SCHEMA_NAME = 'ai_import_mapping' as const;

/** Strict JSON Schema for the Responses `json_schema` block ($schema stripped). */
export const buildAiImportMappingJsonSchema = (): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(
    aiImportMappingOutputSchema,
  ) as Record<string, unknown> & { $schema?: string };
  return rest;
};
